import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { ReactComponent as Menu } from '../../assets/img/icons/header/Menu.svg';
import { ReactComponent as Question } from '../../assets/img/icons/header/Question.svg';
import { ReactComponent as User } from '../../assets/img/icons/header/User.svg';
import { ReactComponent as Logo } from '../../assets/img/icons/header/cfa_logo_without_tm.svg';

import { Notifications, PageDropdown } from '../index';
import AIAssistantButton from '../AIAssistant/AIAssistantButton';

import {
  changeRestaurantUuid,
  getRestaurantAction,
  getRestaurantsAction,
  logoutUser,
} from '../../redux/actions/loginAction';

import {
  AUTHENTIFICATION_LOG,
  BLOCKED_USER,
  CHANGE_EXPIRED_PASSWORD,
  CHICKEN_DASHBOARD,
  LOGIN,
  MULTI_FACTOR_AUTH,
  PASSWORD_RESET,
  SET_PASSWORD,
  SUPPORT_HISTORY,
} from '../../constants/routes.constants';
import { checkSearchParameter, deleteSearchParameters, getSearchParameter } from '../../utils/urlFunctions';
import { hasAccessToPage } from '../../utils/permissions';
import { LocalTimeWidget } from '..';
import { useTimezone } from '../../hooks/useTimezone';
import useRestaurantUuid from '../../hooks/store/useRestaurantUuid';
import useRestaurantTimezone from '../../hooks/store/useRestaurantTimezone';
import useIsMobile from '../../hooks/useIsMobile';
import HeaderRestaurant from './HeaderRestaurant';
import HeaderUnits from './HeaderUnits';
import './Header.scss';

const Header = ({
  login,
  app,
  showMenu,
  logoutUser,
  getRestaurantsAction,
  getRestaurantAction,
  sidebarToggle,
  changeRestaurantUuid,
}) => {
  const history = useHistory();
  const [opened, setOpened] = useState(false);
  
  const isMobile = useIsMobile();
  const restaurantUuid = useRestaurantUuid();
  const timezone = useTimezone(restaurantUuid);
  const restaurantTimezone = useRestaurantTimezone();
  const displayLocalTimeRef = useRef(false);
  
  if (!displayLocalTimeRef.current) {
    if (checkSearchParameter('restaurantUuid')) {
      const queryRestaurantUuid = getSearchParameter('restaurantUuid');
    
      if (queryRestaurantUuid === restaurantUuid && timezone === restaurantTimezone) {
        displayLocalTimeRef.current = true;
      }
    } else {
      displayLocalTimeRef.current = true;
    }
  }

  useEffect(() => {
    if (login.loginInfo && login.loginInfo.daysToPasswordExpiration === 0)
      history.push(CHANGE_EXPIRED_PASSWORD);
    else if (!login.verified) history.push(MULTI_FACTOR_AUTH);
    if (login.userType && login.userType !== 'USER') {
      getRestaurantsAction();
    }
    if (login.userType && login.userType === 'USER' && login.user && login.user.restaurantUuid) {
      changeRestaurantUuid(login.user?.restaurantUuid);
      getRestaurantAction(login.user?.restaurantUuid);
    }
  }, []);

  useEffect(() => {
    if (
      !login.isLoggedIn &&
      ![PASSWORD_RESET, CHANGE_EXPIRED_PASSWORD, MULTI_FACTOR_AUTH, SET_PASSWORD].find((item) =>
        history.location.pathname.includes(item)
      )
    )
      history.push(LOGIN);
    else if (login.loginInfo && login.loginInfo.daysToPasswordExpiration === 0)
      history.push(CHANGE_EXPIRED_PASSWORD);
    else if (!login.verified) history.push(MULTI_FACTOR_AUTH);
    else if (login.isBlocked) history.push(BLOCKED_USER);
    else if (
      login.isLoggedIn &&
      [PASSWORD_RESET, CHANGE_EXPIRED_PASSWORD, MULTI_FACTOR_AUTH, SET_PASSWORD].find((item) =>
        history.location.pathname.includes(item)
      )
    ) {
      history.push(CHICKEN_DASHBOARD);
    }
  }, [login.isLoggedIn, app.currentPage]);

  useEffect(() => {
    login.loginInfo &&
      login.loginInfo.daysToPasswordExpiration === 0 &&
      history.push(CHANGE_EXPIRED_PASSWORD);
  }, [login.loginInfo?.daysToPasswordExpiration]);

  useEffect(() => {
    !login.verified && history.push(MULTI_FACTOR_AUTH);
  }, [login.verified]);

  useEffect(() => {
    if (login.isBlocked) {
      history.push(BLOCKED_USER);
    }
  }, [login.isBlocked]);

  useEffect(() => {
    if (login.userType && login.userType !== 'USER' && localStorage.getItem('restaurantUuid')) {
      changeRestaurantUuid(localStorage.getItem('restaurantUuid'));
    }
  }, [login.userType]);

  useEffect(() => {
    if (
      login.userType &&
      login.userType !== 'USER' &&
      (!localStorage.getItem('restaurantUuid') ||
        localStorage.getItem('restaurantUuid') === 'select' ||
        login.restaurantUuid !== localStorage.getItem('restaurantUuid'))
    ) {
      if (checkSearchParameter('restaurantUuid') || checkSearchParameter('restaurants')) {
        history.push(deleteSearchParameters(['restaurantUuid', 'restaurants']));
      }
      localStorage.setItem('restaurantUuid', login.restaurantUuid);
    }
  }, [login.restaurantUuid]);

  useEffect(() => {
    if (login.userType && login.userType !== 'USER') getRestaurantsAction();
    if (login.userType && login.userType === 'USER' && login.user && login.user.restaurantUuid) {
      changeRestaurantUuid(login.user?.restaurantUuid);
      getRestaurantAction(login.user?.restaurantUuid);
    }
  }, [login.userType, login.user?.restaurantUuid]);

  useEffect(() => {
    if (opened) {
      document.addEventListener('click', checkClick);
    }
  }, [opened]);

  function checkClick(e) {
    if (
      !(e.path || (e.composedPath && e.composedPath())).includes(
        document.getElementById(`navbar_select_restaurant`)
      )
    ) {
      setOpened(false);
      return document.removeEventListener('click', checkClick);
    }
  }

  const handleLogout = () => {
    logoutUser();
    history.push(LOGIN);
  };

  const logoLink = useMemo(() => {
    if (login.allowedPages.length === 0) {
      return CHICKEN_DASHBOARD;
    }

    return login.allowedPages.includes(CHICKEN_DASHBOARD) ? CHICKEN_DASHBOARD : login.allowedPages[0];
  }, [login.allowedPages]);

  return (
    showMenu && (
      <header id="app-header" className="header">
        <div className="header-row">
          <div className="header-main-group">
            <div className="header-main-group__bars header-icons-item" onClick={sidebarToggle}>
              <Menu />
            </div>
    
            <Link className="header-main-group__logo" to={logoLink}>
              <Logo />
            </Link>
    
            {!isMobile ? (
              <>
                <HeaderRestaurant opened={opened} setOpened={setOpened} />
                {displayLocalTimeRef.current ? <LocalTimeWidget timezone={timezone} showTimezone /> : null}
              </>
            ) : null}
          </div>
  
          <div className="header-icons-group">
            <HeaderUnits />

            <AIAssistantButton className="header-icons-item" />

            {login.userType && login.userType === 'ADMIN' && <Notifications />}
    
            {hasAccessToPage(login.userType, login.allowedPages, SUPPORT_HISTORY) && (
              <div className="header-icons-item" onClick={() => history.push(SUPPORT_HISTORY)}>
                <Question />
              </div>
            )}
    
            <PageDropdown
              icon={User}
              options={[
                { label: 'My Account', function: () => history.push(AUTHENTIFICATION_LOG) },
                { label: 'Sign Out', function: () => handleLogout() },
              ]}
            />
          </div>
        </div>
        
        {isMobile ? (
          <div className="header-row header-row-mobile">
            <div className="header-main-group">
              <HeaderRestaurant opened={opened} setOpened={setOpened} />
              {displayLocalTimeRef.current ? <LocalTimeWidget timezone={timezone} showTimezone /> : null}
            </div>
          </div>
        ) : null}
      </header>
    )
  );
};

const mapStateToProps = (state) => ({
  login: state.login,
  app: state.app,
  notifications: state.notifications,
});

const mapDispatchToProps = (dispatch) => {
  return {
    logoutUser: () => dispatch(logoutUser()),
    changeRestaurantUuid: restaurantUuid => dispatch(changeRestaurantUuid(restaurantUuid)),
    getRestaurantsAction: () => dispatch(getRestaurantsAction()),
    getRestaurantAction: (uuid) => dispatch(getRestaurantAction(uuid)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
